<script lang="ts">
    import { onMount, untrack } from 'svelte';
    import { api } from '../lib/api';
    import { envs } from '../lib/stores/environment.svelte';
    import { toast } from '../lib/stores/toast.svelte';
    import type { Consumer, SecretGroup, Secret } from '../lib/types';
    import SecretRow from '../components/SecretRow.svelte';
    import SecretEditor from '../components/SecretEditor.svelte';
    import GroupEditor from '../components/GroupEditor.svelte';
    import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

    const GLOBAL_SCOPE = '__global__';
    const NO_GROUP = '__nogroup__';
    const EXPANDED_KEY = 'keyvault.expandedScopes';

    let consumers = $state<Consumer[]>([]);
    let groups = $state<SecretGroup[]>([]);
    let secrets = $state<Secret[]>([]);
    let loading = $state(false);

    // Track which secret is currently being dragged (for visual feedback)
    let draggingSecretId = $state<string | null>(null);
    // Track which zone is currently being hovered (for highlight)
    let hoverZoneId = $state<string | null>(null);

    function loadExpanded(): Record<string, boolean> {
        try {
            const raw = localStorage.getItem(EXPANDED_KEY);
            if (raw) return JSON.parse(raw);
        } catch {}
        return { [GLOBAL_SCOPE]: true };
    }
    let expanded = $state<Record<string, boolean>>(loadExpanded());

    function saveExpanded() {
        localStorage.setItem(EXPANDED_KEY, JSON.stringify(expanded));
    }

    let editingSecret = $state<Secret | null>(null);
    let creatingInScope = $state<{ appScopeId: string | null; groupId: string | null } | null>(null);

    let editingGroup = $state<SecretGroup | null>(null);
    let creatingGroupInScope = $state<string | null | undefined>(undefined);

    async function load() {
        if (!envs.currentId) return;
        loading = true;
        try {
            const [c, g, s] = await Promise.all([
                api<Consumer[]>(`/api/consumers?environmentId=${envs.currentId}`),
                api<SecretGroup[]>(`/api/groups?environmentId=${envs.currentId}`),
                api<Secret[]>(`/api/secrets?environmentId=${envs.currentId}`),
            ]);
            consumers = c;
            groups = g;
            secrets = s;
        } finally {
            loading = false;
        }
    }

    $effect(() => {
        if (envs.currentId) load();
    });

    onMount(() => {
        if (!envs.currentId) envs.load().then(load);

        // Global monitor watches all drag events on the page.
        // We register draggables and drop targets per-element via Svelte actions below.
        const cleanup = monitorForElements({
            canMonitor: ({ source }) => source.data.type === 'secret',
            onDragStart: ({ source }) => {
                draggingSecretId = source.data.secretId as string;
            },
            onDrop: async ({ location, source }) => {
                const target = location.current.dropTargets[0];
                draggingSecretId = null;
                hoverZoneId = null;
                if (!target) return;

                const sourceData = source.data as {
                    secretId: string;
                    currentScope: string | null;
                    currentGroup: string | null;
                };
                const targetData = target.data as {
                    appScopeId: string | null;
                    groupId: string | null;
                };

                // Did anything actually change?
                if (
                    sourceData.currentScope === targetData.appScopeId &&
                    sourceData.currentGroup === targetData.groupId
                ) {
                    return;
                }

                const moved = secrets.find((s) => s.id === sourceData.secretId);
                if (!moved) return;

                // Optimistic update
                const original = { ...moved };
                moved.appScopeId = targetData.appScopeId;
                moved.groupId = targetData.groupId;
                secrets = [...secrets];

                try {
                    await api(`/api/secrets/${sourceData.secretId}`, {
                        method: 'PATCH',
                        body: JSON.stringify({
                            appScopeId: targetData.appScopeId,
                            groupId: targetData.groupId,
                        }),
                    });
                    toast.success(`Moved ${moved.keyName}`);
                    await load();
                } catch (err: any) {
                    // Roll back optimistic update
                    moved.appScopeId = original.appScopeId;
                    moved.groupId = original.groupId;
                    secrets = [...secrets];
                    toast.error(`Failed to move: ${err.message}`);
                }
            },
        });

        return cleanup;
    });

    function scopeKey(appScopeId: string | null): string {
        return appScopeId ?? GLOBAL_SCOPE;
    }

    function zoneIdFor(appScopeId: string | null, groupId: string | null): string {
        return `${appScopeId ?? GLOBAL_SCOPE}::${groupId ?? NO_GROUP}`;
    }

    function groupsInScope(appScopeId: string | null): SecretGroup[] {
        return groups
            .filter((g) => (g.appScopeId ?? null) === appScopeId)
            .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
    }

    function secretsInZone(appScopeId: string | null, groupId: string | null): Secret[] {
        return secrets
            .filter((s) => (s.appScopeId ?? null) === appScopeId && (s.groupId ?? null) === groupId)
            .sort((a, b) => a.keyName.localeCompare(b.keyName));
    }

    function scopes() {
        return [
            { id: null as string | null, label: '🌐 Global' },
            ...consumers.map((c) => ({ id: c.id as string | null, label: `📱 ${c.name}` })),
        ];
    }

    // === Svelte actions for Pragmatic DnD ===
    // These register/cleanup the draggable + drop target behavior per-element.

    import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

    function makeDraggable(node: HTMLElement, params: { secret: Secret }) {
        let currentParams = params;
        const cleanup = draggable({
            element: node,
            getInitialData: () => ({
                type: 'secret',
                secretId: currentParams.secret.id,
                currentScope: currentParams.secret.appScopeId,
                currentGroup: currentParams.secret.groupId,
            }),
        });
        return {
            update(newParams: { secret: Secret }) {
                currentParams = newParams;
            },
            destroy: cleanup,
        };
    }

    function makeDropZone(
        node: HTMLElement,
        params: { appScopeId: string | null; groupId: string | null }
    ) {
        let currentParams = params;
        const zoneId = zoneIdFor(params.appScopeId, params.groupId);

        const cleanup = dropTargetForElements({
            element: node,
            getData: () => ({
                appScopeId: currentParams.appScopeId,
                groupId: currentParams.groupId,
            }),
            canDrop: ({ source }) => source.data.type === 'secret',
            onDragEnter: () => { hoverZoneId = zoneId; },
            onDragLeave: () => {
                if (hoverZoneId === zoneId) hoverZoneId = null;
            },
        });
        return {
            update(newParams: typeof params) {
                currentParams = newParams;
            },
            destroy: cleanup,
        };
    }

    async function deleteSecret(id: string) {
        if (!confirm('Delete this secret? This cannot be undone.')) return;
        try {
            await api(`/api/secrets/${id}`, { method: 'DELETE' });
            toast.success('Secret deleted');
            load();
        } catch (err: any) {
            toast.error(err.message);
        }
    }

    async function deleteGroup(id: string) {
        if (!confirm('Delete this group? Secrets inside will become ungrouped.')) return;
        try {
            await api(`/api/groups/${id}`, { method: 'DELETE' });
            toast.success('Group deleted');
            load();
        } catch (err: any) {
            toast.error(err.message);
        }
    }

    function toggle(key: string) {
        expanded[key] = !expanded[key];
        saveExpanded();
    }
</script>

<div class="page-header">
    <h1>Secrets</h1>
    <div class="toolbar">
        <button onclick={() => (creatingGroupInScope = null)}>+ Global Group</button>
        <button class="primary" onclick={() => (creatingInScope = { appScopeId: null, groupId: null })}>
            + New Secret
        </button>
    </div>
</div>

{#if loading}
    <p class="muted">Loading...</p>
{:else if !envs.currentId}
    <p class="muted">Select an environment first.</p>
{:else}
    <div class="tree">
        {#each scopes() as scope (scope.id ?? GLOBAL_SCOPE)}
            {@const key = scopeKey(scope.id)}
            {@const groupsHere = groupsInScope(scope.id)}
            {@const ungrouped = secretsInZone(scope.id, null)}
            {@const isOpen = expanded[key] ?? false}
            {@const totalCount = ungrouped.length + groupsHere.reduce((sum, g) => sum + secretsInZone(scope.id, g.id).length, 0)}

            <div class="scope">
                <!-- svelte-ignore a11y_click_events_have_key_events -->
                <!-- svelte-ignore a11y_no_static_element_interactions -->
                <header role="button" tabindex="0" onclick={() => toggle(key)}>
                    <span class="chevron">{isOpen ? '▾' : '▸'}</span>
                    <span class="scope-label">{scope.label}</span>
                    <span class="muted">
            ({groupsHere.length} groups, {totalCount} secrets)
          </span>
                    <span style="flex:1"></span>
                    <button class="ghost" onclick={(e) => { e.stopPropagation(); creatingGroupInScope = scope.id; }}>
                        + Group
                    </button>
                    <button class="ghost" onclick={(e) => { e.stopPropagation(); creatingInScope = { appScopeId: scope.id, groupId: null }; }}>
                        + Secret
                    </button>
                </header>

                {#if isOpen}
                    <div class="scope-body">
                        {#each groupsHere as group (group.id)}
                            {@const gKey = `${key}::${group.id}`}
                            {@const gOpen = expanded[gKey] ?? true}
                            {@const groupSecrets = secretsInZone(scope.id, group.id)}
                            {@const gZoneId = zoneIdFor(scope.id, group.id)}
                            <div class="group">
                                <!-- svelte-ignore a11y_click_events_have_key_events -->
                                <!-- svelte-ignore a11y_no_static_element_interactions -->
                                <header role="button" tabindex="0" onclick={() => toggle(gKey)}>
                                    <span class="chevron">{gOpen ? '▾' : '▸'}</span>
                                    <span class="group-label">📁 {group.name}</span>
                                    <span class="muted">({groupSecrets.length})</span>
                                    <span style="flex:1"></span>
                                    <button class="ghost" onclick={(e) => { e.stopPropagation(); editingGroup = group; }}>Rename</button>
                                    <button class="ghost" onclick={(e) => { e.stopPropagation(); creatingInScope = { appScopeId: scope.id, groupId: group.id }; }}>+ Secret</button>
                                    <button class="ghost" onclick={(e) => { e.stopPropagation(); deleteGroup(group.id); }}>🗑</button>
                                </header>
                                {#if gOpen}
                                    <ul
                                            class="secret-list"
                                            class:drop-hover={hoverZoneId === gZoneId && draggingSecretId}
                                            use:makeDropZone={{ appScopeId: scope.id, groupId: group.id }}
                                    >
                                        {#each groupSecrets as s (s.id)}
                                            <li use:makeDraggable={{ secret: s }} class="draggable" class:dragging={draggingSecretId === s.id}>
                                                <SecretRow secret={s} onEdit={() => (editingSecret = s)} onDelete={() => deleteSecret(s.id)} />
                                            </li>
                                        {/each}
                                        {#if groupSecrets.length === 0}
                                            <li class="empty muted">Drop secrets here.</li>
                                        {/if}
                                    </ul>
                                {/if}
                            </div>
                        {/each}

                        <!-- Ungrouped zone -->
                        <div class="ungrouped">
                            <div class="ungrouped-label muted">Ungrouped</div>
                            <ul
                                    class="secret-list"
                                    class:drop-hover={hoverZoneId === zoneIdFor(scope.id, null) && draggingSecretId}
                                    use:makeDropZone={{ appScopeId: scope.id, groupId: null }}
                            >
                                {#each ungrouped as s (s.id)}
                                    <li use:makeDraggable={{ secret: s }} class="draggable" class:dragging={draggingSecretId === s.id}>
                                        <SecretRow secret={s} onEdit={() => (editingSecret = s)} onDelete={() => deleteSecret(s.id)} />
                                    </li>
                                {/each}
                                {#if ungrouped.length === 0}
                                    <li class="empty muted">Drop secrets here, or create one with "+ Secret".</li>
                                {/if}
                            </ul>
                        </div>
                    </div>
                {/if}
            </div>
        {/each}
    </div>
{/if}

{#if editingSecret || creatingInScope}
    <SecretEditor
            secret={editingSecret}
            initialScope={creatingInScope}
            consumers={consumers}
            groups={groups}
            onClose={() => { editingSecret = null; creatingInScope = null; }}
            onSaved={() => { editingSecret = null; creatingInScope = null; load(); }}
    />
{/if}

{#if editingGroup || creatingGroupInScope !== undefined}
    <GroupEditor
            group={editingGroup}
            appScopeId={creatingGroupInScope ?? null}
            environmentId={envs.currentId!}
            onClose={() => { editingGroup = null; creatingGroupInScope = undefined; }}
            onSaved={() => { editingGroup = null; creatingGroupInScope = undefined; load(); }}
    />
{/if}

<style>
    .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
    .tree { display: flex; flex-direction: column; gap: 8px; }
    .scope { background: var(--bg-1); border: 1px solid var(--border); border-radius: var(--radius-lg); overflow: hidden; }
    .scope > header, .group > header {
        display: flex; align-items: center; gap: 8px;
        padding: 12px 16px; cursor: pointer;
        user-select: none;
    }
    .scope > header:hover, .group > header:hover { background: var(--bg-2); }
    .chevron { display: inline-block; width: 14px; color: var(--text-3); }
    .scope-label { font-weight: 600; font-size: 15px; }
    .group-label { font-weight: 500; }
    .scope-body { padding: 0 8px 8px 32px; }
    .group { border-top: 1px solid var(--border); }
    .secret-list {
        list-style: none; padding: 4px; margin: 0;
        min-height: 40px; border-radius: var(--radius);
        transition: background 0.15s, outline 0.15s;
    }
    .secret-list.drop-hover {
        background: rgba(59, 130, 246, 0.08);
        outline: 2px dashed var(--accent);
        outline-offset: -2px;
    }
    .draggable { cursor: grab; }
    .draggable:active { cursor: grabbing; }
    .draggable.dragging { opacity: 0.35; }
    .ungrouped { padding: 8px 0; border-top: 1px dashed var(--border); margin-top: 4px; }
    .ungrouped-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; padding: 4px 8px; }
    .empty { padding: 12px; font-size: 13px; text-align: center; list-style: none; }
</style>